import type {Palette} from '@github-ui/taillights-worker/document'
import {resolveTaillightsStyleRuns, type TaillightsStyleRun} from '@github-ui/taillights-worker/styles'
import type {TaillightsStylingDirective} from '@github-ui/taillights-worker'
import {taillightsColorVariable} from './taillights-rendering-styles'

type StyledPiece = TaillightsStyleRun & {nativeText: boolean}

const ZERO_WIDTH_JOINER = 0x200d
const VARIATION_SELECTOR_15 = 0xfe0e
const VARIATION_SELECTOR_16 = 0xfe0f
const COMBINING_KEYCAP = 0x20e3

// Non-ASCII text paints in its own color rather than masked, so emoji keep their color glyphs. Plain code units decide
// it, not the runtime's Unicode data, so a server render and its hydration always produce the same pieces. An ASCII
// character joins a following emoji presentation selector or keycap, as in `1️⃣`.
function isNativeText(text: string, index: number) {
  const next = text.charCodeAt(index + 1)
  return text.charCodeAt(index) > 0x7f || next === VARIATION_SELECTOR_16 || next === COMBINING_KEYCAP
}

// Whether the code unit at `index` continues the emoji sequence before it, so a run boundary must not split them.
function continuesSequence(text: string, index: number) {
  const code = text.charCodeAt(index)
  const next = text.charCodeAt(index + 1)
  return (
    (code >= 0xdc00 && code <= 0xdfff) ||
    code === ZERO_WIDTH_JOINER ||
    code === VARIATION_SELECTOR_15 ||
    code === VARIATION_SELECTOR_16 ||
    code === COMBINING_KEYCAP ||
    text.charCodeAt(index - 1) === ZERO_WIDTH_JOINER ||
    (code === 0xd83c && next >= 0xdffb && next <= 0xdfff)
  )
}

// Like the worker, an emoji sequence belongs to the run it starts in, even when a run boundary falls inside it.
function styledPieces(text: string, directives: readonly TaillightsStylingDirective[] | undefined) {
  const pieces: StyledPiece[] = []
  let covered = 0
  for (const run of resolveTaillightsStyleRuns(text.length, directives)) {
    let start = Math.max(run.start, covered)
    while (start < run.end) {
      const nativeText = isNativeText(text, start)
      let end = start + 1
      while (
        end < text.length &&
        (end < run.end ? isNativeText(text, end) === nativeText : nativeText && continuesSequence(text, end))
      ) {
        end++
      }
      pieces.push({...run, start, end, nativeText})
      start = end
    }
    covered = Math.max(covered, start)
  }
  return pieces
}

// A prepared palette colors only its own keys, as the worker paints; without one, colors come from the theme.
function pieceColor(colorKey: string | undefined, palette: Palette | undefined) {
  if (!colorKey) return undefined
  if (palette) return Object.hasOwn(palette.colors, colorKey) ? palette.colors[colorKey] : undefined
  return `var(${taillightsColorVariable(colorKey)}, var(--fgColor-default))`
}

/**
 * Renders source lines as styled text from their directives, without the worker, so a server render shows syntax
 * colors before any script runs. Render it inside an element with the `Paint` and `StyledText` classes so glyphs paint
 * like the bitmap where text masking is supported. `pseudoText` generates all text from attributes, as Safari's bitmap
 * paint does, so browser Find only matches the native source.
 */
export function TaillightsStyledText({
  lines,
  directives,
  palette,
  pseudoText = false,
}: {
  lines: readonly string[]
  directives: ReadonlyArray<readonly TaillightsStylingDirective[] | undefined>
  palette?: Palette
  pseudoText?: boolean
}) {
  let lineStart = 0
  return lines.flatMap((text, index) => {
    const start = lineStart
    lineStart += text.length + 1
    const pieces = styledPieces(text, directives[index]).map(piece => {
      const value = text.slice(piece.start, piece.end)
      const color = pieceColor(piece.colorKey, palette)
      if (!pseudoText && !color && !piece.bold && !piece.italic && !piece.underline && !piece.nativeText) return value
      return (
        <span
          key={start + piece.start}
          data-taillights-pseudo-text={pseudoText || undefined}
          data-taillights-text={pseudoText ? value : undefined}
          data-taillights-native-text={piece.nativeText || undefined}
          style={{
            color,
            fontWeight: piece.bold ? 'bold' : undefined,
            fontStyle: piece.italic ? 'italic' : undefined,
            textDecoration: piece.underline ? 'underline' : undefined,
          }}
        >
          {pseudoText ? null : value}
        </span>
      )
    })
    if (index === 0) return pieces
    if (pseudoText) {
      return [<span key={`newline-${start}`} data-taillights-pseudo-text data-taillights-text={'\n'} />, ...pieces]
    }
    // The HTML parser drops a newline that directly opens a <pre>, and React only restores it for string children.
    const separator = start === 1 ? <span key="leading-newline">{'\n'}</span> : '\n'
    return [separator, ...pieces]
  })
}
